import { Button } from "@mui/material";
import styled, { keyframes } from 'styled-components';
import { media } from '../../theme/breakpoints';

export const MessagesContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    margin-bottom: 20px;
    position: relative;
    background: #ffffff;
    padding-right: 4px;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.15);
        border-radius: 3px;
        transition: background-color 0.2s ease-in-out;
    }

    &:hover::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.15);
    }

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
`

export const Message = styled.div`
    display: flex;
    position: relative;
    margin-bottom: 32px;

    &:last-child {
        margin-bottom: 0;
    }
`;

interface MessageYouProps {
    '$hasAttachment'?: boolean;
}

export const MessageYou = styled(Message)<MessageYouProps>`
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(224, 224, 224, 0.4);
    height: fit-content;
    min-height: 44px;
    width: 100%;
    word-break: break-word;
    font-size: 13px;
    border-radius: 16px;
    margin-bottom: 0px !important;
    ${({'$hasAttachment': hasAttachment}) => hasAttachment && `
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 16px;
    `}

    ${media.sm} {
        font-size: 14px;
    }
`;

export const MessageYouAuthor = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    white-space: nowrap;
    border-radius: 16px;
    font-weight: 600;
    font-size: 13px;
    color: #1f2021;
    background: rgba(224, 224, 224, 1);
    z-index: 50;

    img {
        width: 30px;
        height: 30px;
        object-fit: cover;
    }

    span {
        padding-right: 8px;
    }

    ${media.sm} {
        gap: 10px;
        font-size: 14px;

        img {
            width: 32px;
            height: 32px;
        }
    }

    ${media.md} {
        gap: 12px;

        img {
            width: 35px;
            height: 35px;
        }
    }
`;

export const MessageYouText = styled.div`
    height: fit-content;
    min-height: 44px;
    width: 100%;
    word-break: break-word;
    font-size: 13px;
    border-radius: 16px;
    padding: 8px 10px 8px 10px;
    display: flex;
    align-items: center;
    position: relative;

    &::before {
        content: '';
        background: rgba(224, 224, 224, 0.4);
        width: 40px;
        position: absolute;
    }

    img {
        width: 30px;
        height: 30px;
    }

    ${media.sm} {
        font-size: 14px;

        img {
            width: 32px;
            height: 32px;
        }
    }

    ${media.md} {
        img {
            width: 35px;
            height: 35px;
        }
    }
`;

interface MessageSystemProps {
    '$hasAttachment'?: boolean;
}

export const MessageSystem = styled(Message)<MessageSystemProps>`
    flex-direction: column;
    align-items: flex-start;
    background: rgba(224, 224, 224, 0.4);
    border-radius: 16px;
    padding: 12px;
    margin-bottom: 0 !important;
    ${({'$hasAttachment': hasAttachment}) => hasAttachment && `
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 16px;
    `}

    ${media.sm} {
        padding: 14px;
    }

    ${media.md} {
        padding: 16px;
    }
`;

export const MessageSystemAuthor = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 16px;
    padding: 4px 8px;
    font-weight: 600;
    font-size: 13px;
    background: rgba(224, 224, 224, 1);
    width: fit-content;
    margin-bottom: 8px;

    img {
        width: 45px;
        height: 100%;
    }

    ${media.sm} {
        font-size: 14px;

        img {
            width: 50px;
        }
    }

    ${media.md} {
        img {
            width: 55px;
        }
    }
`;

export const MessageSystemText = styled.div`
    font-weight: 400;
    font-size: 13px;
    word-break: break-word;
    display: flex;
    flex-direction: column;
    margin-top: 5px;
    gap: 8px;
    width: 100%;
    position: relative;

    pre {
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-x: auto;
    }

    pre code {
        display: block;
        max-width: 100%;
        box-sizing: border-box;
        white-space: inherit;
    }

    ${media.sm} {
        font-size: 14px;
    }
`;

export const MessageRate = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    width: 100%;

    span {
        font-size: 12px;
        color: #D9D9D9;
    }

    img {
        cursor: pointer;
        width: 20px;
        height: 20px;
    }
`;

export const MessageSystemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`

export const MessageSystemHeaderIcons = styled.div`
    display: flex;
    position: relative;
    bottom: 4px;
    gap: 4px;
    max-height: 36px;
`

export const StyledButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 16px;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    cursor: pointer;
    transition: background 0.2s ease, box-shadow 0.2s ease;
    font-size: 16px;
    background: transparent;

    svg {
        width: 18px;
        height: 18px;
        min-width: 18px;
        min-height: 18px;
    }

    &:hover {
        background: #e0e0e0;
    }

    &:active {
        background: #d0d0d0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }
`;

const dotFlashing = keyframes`
    0% {
        background-color: #989898;
    }
    50%, 100% {
        background-color: rgba(122, 122, 122, 0.3);
    }
`;

export const LoadingDots = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    height: 20px;
    margin-bottom: 8px;
    width: 100%;
`;

export const Dot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #989898;
    animation: ${dotFlashing} 1.5s infinite linear;

    &:nth-child(1) {
        animation-delay: 0s;
    }

    &:nth-child(2) {
        animation-delay: 0.3s;
    }

    &:nth-child(3) {
        animation-delay: 0.6s;
    }
`;

export const ErrorMessageSystemText = styled(MessageSystemText)`
    color: #ff4d4f;
    font-weight: 600;
`;

const BaseDocumentAttachment = styled.div`
    display: flex;
    align-items: center;
    border-radius: 16px;
    padding: 5px 10px;
    margin-bottom: 8px;
    width: fit-content;
    gap: 5px;
    border: 1px solid #dde2e6;
`;

export const DocumentAttachmentSystem = styled(BaseDocumentAttachment)<{ $hasContentSource?: boolean }>`
    background: rgba(224, 224, 224, 0.4);
    margin-bottom: 0;
    border-radius: 16px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    gap: 7px;
    border: none;
    padding: 5px 12px;
    justify-content: space-between;
    width: fit-content;
    max-width: 100%;
    ${({$hasContentSource}) => $hasContentSource && `
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 16px;
    `}

    ${media.sm} {
        max-width: 90%;
        padding: 5px 16px;
    }

    ${media.md} {
        max-width: 80%;
    }
`;

export const DocumentAttachmentYou = styled(BaseDocumentAttachment)`
    background: rgba(224, 224, 224, 0.4);
    margin-bottom: 0;
    border-radius: 16px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    gap: 7px;
    border: none;
    padding: 5px 12px;
    justify-content: space-between;
    width: fit-content;
    max-width: 100%;

    ${media.sm} {
        max-width: 90%;
        padding: 5px 16px;
    }

    ${media.md} {
        max-width: 80%;
    }
`;

export const DocumentAttachment = styled.div`
    display: flex;
    align-items: center;
    background: #f0f0f0;
    border-radius: 16px;
    padding: 5px 10px;
    margin-bottom: 8px;
    width: fit-content;
    gap: 5px;
    border: 1px solid #dde2e6;
`;

export const DocumentInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const DocumentName = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #1f2021;
    word-break: break-word;

    ${media.sm} {
        font-size: 14px;
    }
`;

export const DocumentAttachmentWrapper = styled.div<{ $show?: boolean }>`
    opacity: ${({$show}) => ($show ? 1 : 0)};
    transition: opacity 0.5s ease-in-out;
    width: fit-content;
    max-width: 100%;

    ${media.sm} {
        max-width: 90%;
    }

    ${media.md} {
        max-width: 80%;
    }
`;

export const StyledDownloadButton = styled(Button)`
    min-width: 30px !important;
    margin-left: 8px !important;

    .MuiButton-icon {
        margin: 0;
    }
`;

export const DocumentContentSourceSystem = styled(BaseDocumentAttachment)<{ $hasAttachment?: boolean }>`
    background: rgba(224, 224, 224, 0.4);
    margin-bottom: 0;
    border-radius: 16px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    gap: 7px;
    border: none;
    padding: 5px 12px;
    justify-content: space-between;
    ${({$hasAttachment}) => $hasAttachment && `
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 16px;
    `}

    ${media.sm} {
        padding: 5px 16px;
    }
`;

export const MessageTimestamp = styled.div`
    font-size: 11px;
    color: #888888;
    text-align: right;
    margin-top: 8px;
    margin-right: 8px;
    width: auto;
    position: relative;
    align-self: flex-start;

    ${media.sm} {
        font-size: 12px;
    }
`;

export const AttachmentFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background: rgba(224, 224, 224, 0.4);
    border-radius: 16px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 5px 12px;
    margin-bottom: 0;

    ${media.sm} {
        padding: 5px 16px;
    }
`;

export const MessageFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    width: 100%;
    margin-bottom: 32px;
`;

export const CitationsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
    width: 100%;
`;

export const CitationsTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #1d2a41;
    margin-bottom: 4px;

    ${media.sm} {
        font-size: 14px;
    }
`;

export const CitationCard = styled.div`
    background: rgba(224, 224, 224, 0.6);
    border-left: 3px solid #1d2a41;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    ${media.sm} {
        padding: 14px;
    }
`;

export const CitationContent = styled.div`
    font-size: 12px;
    color: #1f2021;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;

    ${media.sm} {
        font-size: 13px;
    }
`;

export const CitationButtonsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
    width: 100%;
`;

export const CitationButton = styled.button<{ $isActive?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    border-radius: 12px;
    height: 32px;
    min-height: 32px;
    padding: 0 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 12px;
    font-weight: 600;
    background: ${({ $isActive }) => $isActive ? '#1d2a41' : '#e5e5e5'};
    color: ${({ $isActive }) => $isActive ? '#ffffff' : '#1d2a41'};
    border: 1px solid ${({ $isActive }) => $isActive ? '#1d2a41' : '#e0e0e0'};
    white-space: nowrap;

    i {
        font-size: 11px;
    }

    &:hover {
        background: ${({ $isActive }) => $isActive ? '#1d2a41' : '#e1e1e1'};
        border-color: ${({ $isActive }) => $isActive ? '#152033' : '#d0d0d0'};
    }

    &:active {
        background: ${({ $isActive }) => $isActive ? 'linear-gradient(135deg, #152033 0%, #0f1825 100%)' : '#e8e8e8'};
        transform: translateY(0);
    }

    ${media.sm} {
        font-size: 13px;
        padding: 0 16px;
        
        i {
            font-size: 12px;
        }
    }
`;
